import { FC } from "react";
import { Link } from "react-router-dom";
import { Box, styled, useTheme } from "@mui/system";
import muiIcons from "../../../components/icons/mui-icons";
import { CardContainer } from "../../../components/CardContainer";
import Breadcrumb from "../../../components/breadcrumb/Breadcrumb";
import { GullButton } from "../../../components/buttons/GullButton";
import { H2, H3, H6, Paragraph, Span } from "../../../components/Typography";
import { Avatar, Grid, IconButton, List, ListItem } from "@mui/material";
import {
  userCardData,
  bigImageCardData,
  topTitleCardData,
  sideTitleCardData,
} from "./cards-data";

const StyledBox = styled(Box)(() => ({
  margin: "auto",
  display: "flex",
  flexDirection: "column",
}));

const IconBox = styled(Box)(() => ({
  background: "#6b339921",
  height: "48px",
  width: "48px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  borderRadius: "50%",
}));

const StyledAvatar = styled(Avatar)(() => ({
  width: 80,
  height: 80,
  margin: "auto",
  marginTop: "5px",
  marginBottom: "10px",
}));

const CardHeader = styled(Box)(({ theme }) => ({
  padding: "1rem",
  background: theme.palette.mode === "light" && theme.palette.grey[100],
  borderBottom: `1px solid ${theme.palette.grey[300]}`,
}));

const ImgCardTitle = styled(H3)(() => ({
  position: "relative",
  "&::after": {
    content: '""',
    borderBottom: "1px solid",
    width: "40px",
    display: "block",
    marginTop: "5px",
    position: "absolute",
    right: "44%",
  },
}));

const StyledList = styled(List)(({ theme }) => ({
  "& li": {
    padding: ".8rem 1rem",
    borderTop: `1px solid ${theme.palette.grey[300]}`,
  },
  "& li:last-child": {
    borderBottom: `1px solid ${theme.palette.grey[300]}`,
  },
}));

const AppCards: FC = () => {
  const theme = useTheme()
  const { ChatIcon, FacebookIcon, TwitterIcon, InstagramIcon, CalendarIcon } =
    muiIcons;

  return (
    <>
      <Breadcrumb name="ui-kits" subName="Cards" path="/dashboard/v1" />

      <Grid container spacing={3}>
        {topTitleCardData.map((data) => {
          const { Icon, title, count } = data;
          return (
            <Grid item xs={12} sm={6} md={3}>
              <CardContainer sx={{ display: "flex" }}>
                <Icon sx={{ fontSize: "3.5rem", color: "primary.100" }} />
                <StyledBox alignItems="space-between">
                  <Paragraph fontSize=".8rem">{title}</Paragraph>
                  <H2 fontSize="1.25rem" color="primary.main">
                    {count}
                  </H2>
                </StyledBox>
              </CardContainer>
            </Grid>
          );
        })}
      </Grid>

      <Grid container spacing={3} my={0}>
        <Grid item xs={12} md={6}>
          <Grid container spacing={3}>
            {sideTitleCardData.map((data) => {
              const { Icon, title, subtitle } = data;
              return (
                <Grid item xs={12} sm={4} md={4}>
                  <CardContainer
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                    }}
                  >
                    <IconBox>
                      <Icon
                        sx={{
                          fontSize: "1.2rem",
                          color: "primary.main",
                        }}
                      />
                    </IconBox>
                    <StyledBox alignItems="center">
                      <Paragraph my="5px" fontSize=".8rem">
                        {subtitle}
                      </Paragraph>
                      <H2 fontSize="1.25rem" color="primary.main">
                        {title}
                      </H2>
                    </StyledBox>
                  </CardContainer>
                </Grid>
              );
            })}
          </Grid>

          <Grid container spacing={3} my={0}>
            <Grid item xs={12} md={6}>
              <CardContainer sx={{ textAlign: "center" }}>
                <StyledAvatar src={userCardData.photoUrl} />
                <H6>{userCardData.name}</H6>
                <Paragraph sx={{ fontSize: 12 }}>
                  {userCardData.position}
                </Paragraph>
                <Paragraph sx={{ my: "6px" }}>
                  Create usable interface and designs@uilib.com
                </Paragraph>
                <GullButton rounded={true} variant="contained" color="primary">
                  Hire me
                </GullButton>
                <Box>
                  <IconButton>
                    <FacebookIcon />
                  </IconButton>
                  <IconButton>
                    <TwitterIcon />
                  </IconButton>
                  <IconButton>
                    <InstagramIcon />
                  </IconButton>
                </Box>
              </CardContainer>

              <CardContainer sx={{ p: 0, mt: "25px" }}>
                <CardHeader>
                  <Paragraph>Featured</Paragraph>
                </CardHeader>
                <Box p="1rem">
                  <H3>Card title text</H3>
                  <Paragraph sx={{ py: "15px" }}>
                    With supporting text below as a natural lead-in to
                    additional content.
                  </Paragraph>
                  <GullButton
                    rounded={true}
                    variant="contained"
                    color="primary"
                  >
                    Go Somewhere
                  </GullButton>
                </Box>
              </CardContainer>
            </Grid>

            <Grid item xs={12} md={6}>
              <CardContainer
                sx={{
                  padding: 0,
                  backgroundImage: `url(/assets/images/photo-wide-2.jpg)`,
                  backgroundSize: "cover",
                }}
              >
                <CardContainer
                  sx={{
                    height: 240,
                    background: "#00000059",
                  }}
                >
                  <H3>Card title</H3>
                  <Paragraph py="20px">
                    This is a wider card with supporting text below as a natural
                    lead-in to additional content. This content is a little bit
                    longer.
                  </Paragraph>
                  <Paragraph>Last updated 3 mins ago</Paragraph>
                </CardContainer>
              </CardContainer>

              <CardContainer sx={{ mt: "25px" }}>
                <H3>Card title</H3>
                <Paragraph py="20px">
                  This is a wider card with supporting text below as a natural
                  lead-in to additional content. This content is a little bit
                  longer.
                </Paragraph>
                <Paragraph sx={{ display: "flex" }}>
                  <Link to="dashboard/v1">
                    <Span mr="20px" color="primary.main">
                      Card link
                    </Span>
                  </Link>
                  <Link to="dashboard/v1">
                    <Span color="primary.main">Another link</Span>
                  </Link>
                </Paragraph>
              </CardContainer>
            </Grid>
          </Grid>
        </Grid>

        <Grid item xs={12} md={6}>
          <Grid container spacing={3}>
            {bigImageCardData.map((data) => {
              const { title, subtitle, photoUrl, comment, date } = data;
              return (
                <Grid item xs={12} md={6}>
                  <CardContainer
                    sx={{
                      padding: 0,
                      backgroundImage: `url(${photoUrl})`,
                      backgroundSize: "cover",
                    }}
                  >
                    <CardContainer
                      sx={{
                        height: 550,
                        background: "#00000059",
                        color: "text.secondary",
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "space-between",
                      }}
                    >
                      <Box textAlign="center">
                        <ImgCardTitle>{title}</ImgCardTitle>
                        <Paragraph py="20px">{subtitle}</Paragraph>
                      </Box>
                      <Box display="flex" alignItems="center">
                        <ChatIcon sx={{ fontSize: 16, mr: "5px" }} />
                        {comment}
                        <CalendarIcon
                          sx={{ fontSize: 16, ml: "20px", mr: "5px" }}
                        />
                        {date}
                      </Box>
                    </CardContainer>
                  </CardContainer>
                </Grid>
              );
            })}

            <Grid item xs={12} md={6}>
              <CardContainer sx={{ p: 0 }}>
                <img
                  alt=""
                  width="100%"
                  src="/assets/images/photo-wide-2.jpg"
                />
                <Paragraph sx={{ p: "1rem", pb: "2rem" }}>
                  This is a wider card with supporting text below as a natural
                  lead-in to additional content. This content is a little bit
                  longer.
                </Paragraph>
              </CardContainer>
            </Grid>

            <Grid item xs={12} md={6}>
              <CardContainer sx={{ p: 0 }}>
                <img
                  alt=""
                  width="100%"
                  src="/assets/images/photo-wide-3.jpg"
                />
                <Box p="1rem">
                  <H3>Card title</H3>
                  <Paragraph pt="20px">
                    This is a wider card with supporting text below as a natural
                    lead-in to additional content. This content is a little bit
                    longer.
                  </Paragraph>
                </Box>
                <StyledList>
                  <ListItem>Cras justo odio</ListItem>
                  <ListItem>Dapibus ac facilisis in</ListItem>
                  <ListItem>Vestibulum at eros</ListItem>
                </StyledList>
                <Box p="1rem">
                  <Paragraph sx={{ display: "flex" }}>
                    <Link to="dashboard/v1">
                      <Span mr="20px" color="primary.main">
                        Card link
                      </Span>
                    </Link>
                    <Link to="dashboard/v1">
                      <Span color="primary.main">Another link</Span>
                    </Link>
                  </Paragraph>
                </Box>
              </CardContainer>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </>
  );
};

export default AppCards;
